// src/pages/Events.jsx
import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, orderBy, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import LoadingSpinner from '../components/LoadingSpinner';

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    eventDate: '',
    venue: '',
    active: true,
    banner: ''
  });
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState('');
  const [currentTime, setCurrentTime] = useState('');
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }));
    };
    updateClock();
    const clockInterval = setInterval(updateClock, 1000);

    const q = query(collection(db, 'events'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const eventsData = [];
      snapshot.forEach((doc) => {
        eventsData.push({ id: doc.id, ...doc.data() });
      });
      setEvents(eventsData);
      setLoading(false);
    });

    return () => {
      clearInterval(clockInterval);
      unsubscribe();
    };
  }, []);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      eventDate: '',
      venue: '',
      active: true,
      banner: ''
    });
    setBannerFile(null);
    setBannerPreview('');
    setEditingEvent(null);
    setUploadProgress(0);
    setIsUploading(false);
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openEditModal = (item) => {
    setEditingEvent(item);
    const eventDate = item.eventDate?.toDate ? item.eventDate.toDate() : new Date(item.eventDate);
    setFormData({
      title: item.title || '',
      description: item.description || '',
      eventDate: eventDate ? eventDate.toISOString().slice(0, 16) : '',
      venue: item.venue || '',
      active: item.active !== false,
      banner: item.banner || ''
    });
    if (item.banner) setBannerPreview(item.banner);
    setModalOpen(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBannerFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setBannerPreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setIsUploading(true);

    try {
      let bannerUrl = formData.banner;

      if (bannerFile) {
        const storageRef = ref(storage, `events/${Date.now()}_${bannerFile.name}`);
        const uploadTask = uploadBytesResumable(storageRef, bannerFile);
        await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
            },
            (error) => reject(error),
            async () => {
              bannerUrl = await getDownloadURL(uploadTask.snapshot.ref);
              resolve();
            }
          );
        });
      }

      const eventData = {
        title: formData.title,
        description: formData.description,
        eventDate: new Date(formData.eventDate),
        venue: formData.venue,
        active: formData.active,
        banner: bannerUrl,
        updatedAt: new Date()
      };

      if (editingEvent) {
        if (formData.banner && bannerFile && formData.banner !== bannerUrl) {
          try { await deleteObject(ref(storage, formData.banner)); } catch (e) {}
        }
        await updateDoc(doc(db, 'events', editingEvent.id), eventData);
        alert('Event updated successfully!');
      } else {
        eventData.createdAt = new Date();
        await addDoc(collection(db, 'events'), eventData);
        alert('Event created successfully!');
      }

      setModalOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving event:', error);
      alert('Error saving event: ' + error.message);
    } finally {
      setLoading(false);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (id, bannerUrl) => {
    if (!window.confirm('Confirm execution of destructive command on targeted event data path?')) return;
    
    setLoading(true);
    try {
      if (bannerUrl) {
        try { await deleteObject(ref(storage, bannerUrl)); } catch (e) {}
      }
      await deleteDoc(doc(db, 'events', id));
      alert('Event data deleted.');
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Error deleting event: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    resetForm();
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Date TBD';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch {
      return 'Invalid date';
    }
  };

  const getStats = () => {
    const total = events.length;
    const active = events.filter(e => e.active === true).length;
    const upcoming = events.filter(e => {
      if (!e.eventDate) return false;
      const date = e.eventDate.toDate ? e.eventDate.toDate() : new Date(e.eventDate);
      return date > new Date();
    }).length;
    return { total, active, upcoming };
  };

  const stats = getStats();

  const styles = `
    .events-page {
      animation: structuralSlideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
    }

    .management-toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 32px;
      background: rgba(255, 255, 255, 0.3);
      border: 1px solid rgba(255, 255, 255, 0.6);
      padding: 16px 24px;
      border-radius: 16px;
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      flex-wrap: wrap;
      gap: 16px;
    }

    .view-toggle-group {
      display: flex;
      gap: 8px;
    }

    .toggle-action-btn {
      padding: 10px 20px;
      border: 2px solid rgba(14, 165, 233, 0.2);
      background: rgba(255, 255, 255, 0.5);
      border-radius: 12px;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.85rem;
      color: #334155;
      transition: all 0.2s ease;
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }

    .toggle-action-btn:hover {
      background: rgba(255, 255, 255, 0.8);
      border-color: #0ea5e9;
    }

    .toggle-action-btn.active {
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      color: white;
      border-color: #0ea5e9;
      box-shadow: 0 4px 15px rgba(14, 165, 233, 0.3);
    }

    .btn-action-trigger {
      padding: 12px 24px;
      border: none;
      border-radius: 12px;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.85rem;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      box-shadow: 0 4px 15px rgba(14, 165, 233, 0.2);
    }

    .btn-action-trigger:hover {
      transform: translateY(-3px);
    }

    .btn-blue {
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      color: white;
    }

    .btn-blue:hover {
      box-shadow: 0 10px 25px rgba(14, 165, 233, 0.4);
    }

    .events-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
      gap: 28px;
    }

    .event-card {
      background: rgba(255, 255, 255, 0.45);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border-radius: 16px;
      overflow: hidden;
      border: 1px solid rgba(255, 255, 255, 0.6);
      box-shadow: 0 8px 32px 0 rgba(14, 165, 233, 0.06);
      transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    }

    .event-card:hover {
      transform: translateY(-6px);
      background: rgba(255, 255, 255, 0.65);
      box-shadow: 0 16px 36px 0 rgba(14, 165, 233, 0.14);
      border-color: rgba(14, 165, 233, 0.3);
    }

    .event-card-banner-frame {
      width: 100%;
      height: 200px;
      object-fit: cover;
      background: linear-gradient(135deg, #0f172a, #334155);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 44px;
      position: relative;
    }

    .event-card-banner-frame img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .status-badge-indicator {
      position: absolute;
      top: 16px;
      right: 16px;
      padding: 4px 12px;
      border-radius: 6px;
      font-size: 0.7rem;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      border: 1px solid rgba(255,255,255,0.2);
      backdrop-filter: blur(4px);
    }

    .event-card-content {
      padding: 24px;
      display: flex;
      flex-direction: column;
      flex: 1;
    }

    .event-card-title {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.25rem;
      font-weight: 700;
      color: #0f172a;
      margin-bottom: 10px;
    }

    .event-card-description {
      color: #334155;
      font-size: 0.88rem;
      line-height: 1.5;
      margin-bottom: 20px;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .event-card-details-row {
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin-bottom: 20px;
      padding: 14px;
      background: rgba(255, 255, 255, 0.3);
      border-radius: 12px;
      border: 1px solid rgba(255,255,255,0.3);
    }

    .event-detail-unit {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 0.85rem;
      font-weight: 700;
      color: #0f172a;
    }

    .event-detail-unit i {
      width: 20px;
      font-size: 16px;
    }

    .event-card-actions {
      display: flex;
      gap: 10px;
      padding-top: 16px;
      border-top: 1px solid rgba(14, 165, 233, 0.1);
      margin-top: auto;
    }

    .btn-row-action {
      flex: 1;
      padding: 8px 14px;
      border-radius: 8px;
      font-weight: 700;
      font-size: 0.8rem;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      transition: all 0.2s;
      border: none;
    }

    .btn-row-edit {
      background: rgba(14, 165, 233, 0.12);
      border: 1px solid rgba(14, 165, 233, 0.3);
      color: #0ea5e9;
    }

    .btn-row-edit:hover {
      background: #0ea5e9;
      color: white;
    }

    .btn-row-delete {
      background: rgba(244, 63, 94, 0.1);
      border: 1px solid rgba(244, 63, 94, 0.25);
      color: #f43f5e;
    }

    .btn-row-delete:hover {
      background: #f43f5e;
      color: white;
    }

    .empty-state {
      text-align: center;
      padding: 60px 20px;
      background: rgba(255, 255, 255, 0.45);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border-radius: 16px;
      border: 1px solid rgba(255, 255, 255, 0.6);
      grid-column: 1/-1;
    }

    .empty-state i {
      font-size: 54px;
      color: #64748b;
      margin-bottom: 16px;
      display: block;
    }

    .empty-state h3 {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.2rem;
      margin-bottom: 6px;
      color: #0f172a;
    }

    .empty-state p {
      color: #64748b;
      font-size: 0.88rem;
      margin-bottom: 20px;
    }

    /* Modal Styles - Positioned at Top */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      min-height: 100vh;
      background: rgba(15, 23, 42, 0.6);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      z-index: 10000;
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding: 40px 20px;
      overflow-y: auto;
      animation: fadeIn 0.3s ease;
    }

    .modal-viewport-box {
      background: rgba(255, 255, 255, 0.98);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border: 1px solid rgba(255, 255, 255, 0.6);
      border-radius: 24px;
      padding: 40px;
      width: 100%;
      max-width: 650px;
      max-height: none;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      animation: slideDown 0.3s ease;
    }

    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .modal-viewport-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      padding-bottom: 16px;
      border-bottom: 2px solid rgba(14, 165, 233, 0.1);
    }

    .modal-viewport-header h2 {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.4rem;
      color: #0f172a;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .modal-viewport-header h2 i {
      color: #0ea5e9;
    }

    .modal-close-btn {
      width: 40px;
      height: 40px;
      background: rgba(244, 63, 94, 0.1);
      border: 1px solid rgba(244, 63, 94, 0.25);
      border-radius: 12px;
      color: #f43f5e;
      cursor: pointer;
      font-size: 1.2rem;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .modal-close-btn:hover {
      background: #f43f5e;
      color: white;
      transform: rotate(90deg);
    }

    .form-group {
      margin-bottom: 20px;
    }

    .form-group label {
      display: block;
      margin-bottom: 8px;
      font-weight: 700;
      font-size: 0.85rem;
      color: #334155;
    }

    .form-group label .required {
      color: #f43f5e;
      margin-left: 4px;
    }

    .form-group input,
    .form-group textarea,
    .form-group select {
      width: 100%;
      padding: 12px 16px;
      border: 2px solid rgba(14, 165, 233, 0.15);
      border-radius: 12px;
      font-size: 14px;
      background: rgba(255, 255, 255, 0.6);
      color: #0f172a;
      outline: none;
      transition: all 0.3s ease;
      font-family: inherit;
    }

    .form-group input:focus,
    .form-group textarea:focus,
    .form-group select:focus {
      border-color: #0ea5e9;
      background: #ffffff;
      box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.1);
    }

    .form-group textarea {
      resize: vertical;
      min-height: 80px;
    }

    .file-upload-wrapper {
      position: relative;
      border: 2px dashed rgba(14, 165, 233, 0.3);
      border-radius: 12px;
      padding: 16px;
      text-align: center;
      cursor: pointer;
      transition: all 0.3s ease;
      background: rgba(255, 255, 255, 0.3);
    }

    .file-upload-wrapper:hover {
      border-color: #0ea5e9;
      background: rgba(255, 255, 255, 0.5);
    }

    .file-upload-wrapper input[type="file"] {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      cursor: pointer;
    }

    .file-upload-wrapper .upload-icon {
      font-size: 1.5rem;
      color: #0ea5e9;
      margin-bottom: 4px;
      display: block;
    }

    .file-upload-wrapper .upload-text {
      color: #64748b;
      font-size: 0.8rem;
      font-weight: 600;
    }

    .file-upload-wrapper .upload-text span {
      color: #0ea5e9;
      font-weight: 700;
    }

    .image-preview-wrapper {
      margin-top: 12px;
      position: relative;
      display: inline-block;
      border-radius: 12px;
      overflow: hidden;
      border: 2px solid rgba(14, 165, 233, 0.2);
    }

    .image-preview-wrapper img {
      width: 120px;
      height: 120px;
      object-fit: cover;
      display: block;
    }

    .image-preview-wrapper .remove-image {
      position: absolute;
      top: 4px;
      right: 4px;
      background: rgba(244, 63, 94, 0.9);
      color: white;
      border: none;
      border-radius: 50%;
      width: 24px;
      height: 24px;
      cursor: pointer;
      font-size: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
    }

    .image-preview-wrapper .remove-image:hover {
      transform: scale(1.1);
      background: #f43f5e;
    }

    .upload-progress {
      margin-top: 12px;
      background: rgba(14, 165, 233, 0.1);
      border-radius: 8px;
      height: 6px;
      overflow: hidden;
    }

    .upload-progress .progress-bar {
      height: 100%;
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      transition: width 0.3s ease;
      border-radius: 8px;
    }

    .checkbox-wrapper {
      display: flex;
      align-items: center;
      gap: 10px;
      cursor: pointer;
      padding: 4px 0;
    }

    .checkbox-wrapper input[type="checkbox"] {
      width: 18px;
      height: 18px;
      accent-color: #0ea5e9;
      cursor: pointer;
    }

    .checkbox-wrapper label {
      font-weight: 700;
      font-size: 0.85rem;
      color: #334155;
      cursor: pointer;
      margin: 0;
    }

    .modal-footer {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      margin-top: 24px;
      padding-top: 16px;
      border-top: 2px solid rgba(14, 165, 233, 0.1);
    }

    .btn-cancel {
      padding: 10px 24px;
      background: rgba(255, 255, 255, 0.4);
      border: 2px solid rgba(14, 165, 233, 0.2);
      border-radius: 12px;
      color: #334155;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.9rem;
      transition: all 0.3s ease;
    }

    .btn-cancel:hover {
      background: #ffffff;
      border-color: #334155;
      transform: translateY(-2px);
    }

    .btn-submit {
      padding: 10px 28px;
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      color: white;
      border: none;
      border-radius: 12px;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.9rem;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;
      gap: 10px;
    }

    .btn-submit:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 10px 25px rgba(14, 165, 233, 0.4);
    }

    .btn-submit:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .btn-submit .spinner {
      width: 18px;
      height: 18px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top-color: white;
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    @media (max-width: 768px) {
      .events-grid {
        grid-template-columns: 1fr;
      }
      .view-toggle-group {
        justify-content: center;
      }
      .management-toolbar {
        flex-direction: column;
        align-items: stretch;
      }
      .management-toolbar > div {
        justify-content: center;
      }
      .modal-viewport-box {
        padding: 24px;
        margin: 10px;
      }
      .modal-viewport-header h2 {
        font-size: 1.2rem;
      }
      .modal-footer {
        flex-direction: column;
      }
      .modal-footer button {
        width: 100%;
        justify-content: center;
      }
    }
  `;

  if (loading && events.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <style>{styles}</style>
      <div className="events-page">
        <div className="welcome-hero">
          <h1>📅 Events Management</h1>
          <p>Schedule college functions, view time calendars, and manage activity campaigns seamlessly.</p>
          <div className="hero-meta-date">
            <i className="far fa-clock"></i> {currentTime}
          </div>
        </div>

        <div className="stats-matrix" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="matrix-card card-blue">
            <div>
              <div className="matrix-value">{stats.total}</div>
              <div className="matrix-label">Total Events</div>
            </div>
            <div className="matrix-icon-box"><i className="fas fa-calendar-alt"></i></div>
          </div>
          <div className="matrix-card card-emerald">
            <div>
              <div className="matrix-value">{stats.active}</div>
              <div className="matrix-label">Active Events</div>
            </div>
            <div className="matrix-icon-box"><i className="fas fa-toggle-on"></i></div>
          </div>
          <div className="matrix-card card-amber">
            <div>
              <div className="matrix-value">{stats.upcoming}</div>
              <div className="matrix-label">Upcoming Events</div>
            </div>
            <div className="matrix-icon-box"><i className="fas fa-hourglass-start"></i></div>
          </div>
        </div>

        <div className="management-toolbar">
          <div className="view-toggle-group">
            <button 
              className={`toggle-action-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              <i className="fas fa-grid-2"></i> Grid View
            </button>
            <button 
              className={`toggle-action-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <i className="fas fa-calendar-days"></i> Calendar View
            </button>
          </div>
          <button className="btn-action-trigger btn-blue" onClick={openCreateModal}>
            <i className="fas fa-plus-circle"></i> Create New Event
          </button>
        </div>

        <div className="events-grid">
          {events.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-calendar-xmark"></i>
              <h3>No Events Documented</h3>
              <p>The database register holds zero scheduled records at this layer path.</p>
              <button className="btn-action-trigger btn-blue" onClick={openCreateModal}>
                <i className="fas fa-plus-circle"></i> Create First Event
              </button>
            </div>
          ) : (
            events.map((item) => (
              <div className="event-card" key={item.id}>
                <div className="event-card-banner-frame">
                  {item.banner ? (
                    <img src={item.banner} alt="Banner" />
                  ) : (
                    <i className="fas fa-calendar-days" style={{ fontSize: '44px', opacity: 0.5 }}></i>
                  )}
                  <div className="status-badge-indicator" style={{
                    background: item.active ? 'rgba(16,185,129,0.4)' : 'rgba(100,116,139,0.4)'
                  }}>
                    {item.active ? 'Active' : 'Inactive'}
                  </div>
                </div>
                <div className="event-card-content">
                  <h3 className="event-card-title">{item.title || 'Untitled Event'}</h3>
                  <p className="event-card-description">{item.description || 'No descriptive context allocated to event.'}</p>
                  <div className="event-card-details-row">
                    <div className="event-detail-unit" style={{ color: '#0ea5e9' }}>
                      <i className="fas fa-clock"></i>
                      <span>{formatDate(item.eventDate)}</span>
                    </div>
                    <div className="event-detail-unit" style={{ color: '#f59e0b', marginTop: '6px' }}>
                      <i className="fas fa-location-dot"></i>
                      <span>{item.venue || 'Venue TBD'}</span>
                    </div>
                  </div>
                  <div className="event-card-actions">
                    <button className="btn-row-action btn-row-edit" onClick={() => openEditModal(item)}>
                      <i className="fas fa-pen-to-square"></i> Edit
                    </button>
                    <button className="btn-row-action btn-row-delete" onClick={() => handleDelete(item.id, item.banner)}>
                      <i className="fas fa-trash-can"></i> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Event Modal - Positioned at Top */}
        {modalOpen && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-viewport-box" onClick={(e) => e.stopPropagation()}>
              <div className="modal-viewport-header">
                <h2>
                  <i className={`fas ${editingEvent ? 'fa-edit' : 'fa-plus-circle'}`}></i>
                  {editingEvent ? 'Edit Event Details' : 'Create New Event'}
                </h2>
                <button className="modal-close-btn" onClick={closeModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Event Title <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Enter event title"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Description <span className="required">*</span></label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="3"
                    placeholder="Enter event specifications..."
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Event Date & Time <span className="required">*</span></label>
                  <input
                    type="datetime-local"
                    value={formData.eventDate}
                    onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Venue <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.venue}
                    onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                    placeholder="e.g., Main Auditorium, Lab Center Hub"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Event Banner Image</label>
                  <div className="file-upload-wrapper">
                    <i className="fas fa-cloud-upload-alt upload-icon"></i>
                    <div className="upload-text">
                      Drag & drop or <span>browse</span> to upload banner
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleBannerChange}
                    />
                  </div>
                  {bannerPreview && (
                    <div className="image-preview-wrapper">
                      <img src={bannerPreview} alt="Preview" />
                      <button
                        type="button"
                        className="remove-image"
                        onClick={() => {
                          setBannerPreview('');
                          setBannerFile(null);
                          setFormData({ ...formData, banner: '' });
                        }}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  )}
                </div>

                <div className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    id="activeCheckbox"
                  />
                  <label htmlFor="activeCheckbox">Active (Show on live website)</label>
                </div>

                {isUploading && uploadProgress > 0 && (
                  <div className="upload-progress">
                    <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                  </div>
                )}

                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={closeModal}>
                    <i className="fas fa-times"></i> Cancel
                  </button>
                  <button type="submit" className="btn-submit" disabled={isUploading}>
                    {isUploading ? (
                      <>
                        <span className="spinner"></span>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save"></i>
                        {editingEvent ? 'Update Event' : 'Create Event'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Events;